import { useState, useEffect } from 'react';
import { YouTubeEmbed } from '../../../components/YouTubeEmbed';
import { BandcampEmbed } from '../../../components/BandcampEmbed';
import { PlatformLinks, type AnyLinks, getSearchLinks } from '../../../components/PlatformLinks';
import { BClickableArtistName } from '../BClickableArtistName';
import { useArtistData } from '../../../context/ArtistDataContext';
import './BArtistCard.css';

interface BandcampEmbedData {
  embedHtml: string;
  albumId?: string;
  trackId?: string;
  title?: string;
  bandcampUrl?: string;
}

interface Artist {
  name: string;
  track?: string;
  tracks?: string[];
  description?: string;
  matchReason?: string;
  location?: string | null;
  area?: string;
  youtubeVideoId?: string;
  bandcampEmbed?: BandcampEmbedData | string;
  links?: Record<string, string | undefined>;
  members?: Array<{ name: string; role?: string }>;
}

interface BArtistCardProps {
  artist: Artist;
  number?: number;
  isFeatured?: boolean;
  onArtistClick?: (artistName: string) => void;
}

/**
 * Artist card with new design, full enrichment from API.
 */
export function BArtistCard({
  artist,
  number,
  isFeatured = false,
  onArtistClick,
}: BArtistCardProps) {
  const [videoReady, setVideoReady] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const tracks = artist.tracks || (artist.track ? [artist.track] : []);

  // Fetch enriched data from API (cached, versioned)
  const { data: artistData } = useArtistData(artist.name, tracks);

  // Resolve embeds: API is source of truth, props are fallback while loading
  const youtubeVideoId = artistData
    ? (artistData._meta?.youtubeRejected ? undefined : artistData.youtubeVideoId)
    : artist.youtubeVideoId;

  const bandcampEmbed = artistData
    ? (artistData._meta?.bandcampRejected ? undefined : artistData.bandcampEmbed)
    : (typeof artist.bandcampEmbed === 'string' ? undefined : artist.bandcampEmbed);

  // Toggle between YouTube and Bandcamp (default to Bandcamp when available)
  const [showBandcamp, setShowBandcamp] = useState(!!bandcampEmbed);

  // Sync showBandcamp when bandcampEmbed availability changes
  useEffect(() => {
    if (!bandcampEmbed && showBandcamp) {
      setShowBandcamp(false);
    }
    if (bandcampEmbed && !youtubeVideoId) {
      setShowBandcamp(true);
    }
  }, [bandcampEmbed, youtubeVideoId, showBandcamp]);

  // Determine which embeds are available
  const hasYouTube = !!youtubeVideoId;
  const hasBandcamp = !!bandcampEmbed;
  const hasBoth = hasYouTube && hasBandcamp;
  const willShowEmbed = tracks.length > 0;
  const willShowYouTube = hasYouTube || (!hasYouTube && !hasBandcamp && willShowEmbed);

  // Links: API is source of truth when available
  const searchLinks = getSearchLinks(artist.name);
  const apiLinks = (artistData?.links || {}) as Record<string, string | undefined>;
  const links: AnyLinks = artistData
    ? {
        bandcamp: apiLinks.bandcamp || bandcampEmbed?.bandcampUrl,
        spotify: apiLinks.spotify || searchLinks.spotify,
        youtube: apiLinks.youtube,
        apple: apiLinks.apple || searchLinks.apple,
        tidal: apiLinks.tidal || searchLinks.tidal,
        amazon: searchLinks.amazon,
        soundcloud: apiLinks.soundcloud,
        discogs: apiLinks.discogs,
        deezer: apiLinks.deezer,
        official: apiLinks.official,
        instagram: apiLinks.instagram,
        twitter: apiLinks.twitter,
        facebook: apiLinks.facebook,
      }
    : {
        bandcamp: artist.links?.bandcamp || (typeof artist.bandcampEmbed === 'object' ? artist.bandcampEmbed?.bandcampUrl : undefined) || searchLinks.bandcamp,
        spotify: artist.links?.spotify || searchLinks.spotify,
        youtube: artist.links?.youtube,
        apple: searchLinks.apple,
        tidal: searchLinks.tidal,
        amazon: searchLinks.amazon,
      };

  // Members: API is source of truth when available
  const members = artistData?.members || artist.members || [];
  const memberOf = artistData?.memberOf || [];

  // Get display values
  const trackName = tracks[0] || '';
  const description = artist.description || artist.matchReason || '';
  const locationName = artist.location || artist.area || artistData?.area || '';

  // Fallback timeout to show card details
  useEffect(() => {
    if (tracks.length === 0) {
      setVideoReady(true);
      return;
    }
    const timeout = setTimeout(() => setVideoReady(true), 3000);
    return () => clearTimeout(timeout);
  }, [tracks.length]);

  const cardClass = isFeatured ? 'b-artist-card b-artist-card--featured' : 'b-artist-card';

  return (
    <article className={`${cardClass} ${videoReady ? 'ready' : 'loading'}`}>
      {number !== undefined && (
        <span className={`b-artist-card__number ${isFeatured ? 'b-artist-card__number--large' : ''}`}>
          {String(number).padStart(2, '0')}
        </span>
      )}

      {/* Bookmark button */}
      <button
        className={`b-artist-card__bookmark ${isBookmarked ? 'b-artist-card__bookmark--active' : ''}`}
        onClick={() => setIsBookmarked(!isBookmarked)}
        title={isBookmarked ? 'Remove bookmark' : 'Bookmark artist'}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {/* Video embed with toggle - FIRST like old UI */}
      <div className="b-artist-card__embed">
        {/* Toggle row */}
        {willShowEmbed && (
          <div className={`b-artist-card__embed-toggle ${!hasBoth ? 'single' : ''}`}>
            {hasBoth ? (
              <>
                <button
                  className={`b-artist-card__toggle-btn ${!showBandcamp ? 'active' : ''}`}
                  onClick={() => setShowBandcamp(false)}
                  title="YouTube"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </button>
                <button
                  className={`b-artist-card__toggle-btn ${showBandcamp ? 'active' : ''}`}
                  onClick={() => setShowBandcamp(true)}
                  title="Bandcamp"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
                  </svg>
                </button>
              </>
            ) : (
              <span className="b-artist-card__toggle-btn active" title={willShowYouTube ? 'YouTube' : 'Bandcamp'}>
                {willShowYouTube ? (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
                  </svg>
                )}
              </span>
            )}
          </div>
        )}

        {/* Embed container */}
        {tracks.length > 0 && (
          <div className="b-artist-card__embed-container">
            {hasYouTube && !showBandcamp && (
              <YouTubeEmbed
                query={`${artist.name} ${trackName}`}
                artistName={artist.name}
                trackName={trackName}
                onReady={() => setVideoReady(true)}
                tryBandcamp={false}
                preloadedVideoId={youtubeVideoId}
              />
            )}

            {hasBandcamp && (showBandcamp || !hasYouTube) && (
              <BandcampEmbed
                artistName={artist.name}
                trackName={trackName}
                preloadedEmbed={bandcampEmbed}
                onReady={() => setVideoReady(true)}
                size="medium"
              />
            )}

            {!hasYouTube && !hasBandcamp && (
              <YouTubeEmbed
                query={`${artist.name} ${trackName}`}
                artistName={artist.name}
                trackName={trackName}
                onReady={() => setVideoReady(true)}
                tryBandcamp={true}
              />
            )}
          </div>
        )}
      </div>

      {/* Card info - AFTER embed like old UI */}
      <div className="b-artist-card__info">
        <h3
          className="b-artist-card__name"
          onClick={() => onArtistClick?.(artist.name)}
          style={{ cursor: onArtistClick ? 'pointer' : 'default' }}
        >
          {artist.name}
        </h3>

        {trackName && (
          <p className="b-artist-card__track">"{trackName}"</p>
        )}

        {description && (
          <p className="b-artist-card__desc">{description}</p>
        )}

        {locationName && (
          <span className="b-artist-card__location">{locationName}</span>
        )}

        {/* Platform links with colored icons */}
        <div className="b-artist-card__links">
          <PlatformLinks
            links={links}
            artistName={artist.name}
            size="medium"
            showSearchFallbacks={true}
          />
        </div>

        {/* Band members - clickable chips */}
        {members.length > 0 && (
          <div className="b-artist-card__members">
            <span className="b-artist-card__members-label">Members:</span>
            <div className="b-artist-card__members-list">
              {members.slice(0, 5).map((member, idx) => (
                onArtistClick ? (
                  <button
                    key={idx}
                    className="b-artist-card__member-chip"
                    onClick={() => onArtistClick(member.name)}
                  >
                    {member.name}
                  </button>
                ) : (
                  <BClickableArtistName
                    key={idx}
                    name={member.name}
                    className="b-artist-card__member-chip"
                  />
                )
              ))}
              {members.length > 5 && (
                <span className="b-artist-card__member-more">+{members.length - 5}</span>
              )}
            </div>
          </div>
        )}

        {/* Also in (other bands) - clickable chips */}
        {memberOf.length > 0 && (
          <div className="b-artist-card__members">
            <span className="b-artist-card__members-label">Also in:</span>
            <div className="b-artist-card__members-list">
              {memberOf.slice(0, 5).map((bandName, idx) => (
                onArtistClick ? (
                  <button
                    key={idx}
                    className="b-artist-card__member-chip"
                    onClick={() => onArtistClick(bandName)}
                  >
                    {bandName}
                  </button>
                ) : (
                  <BClickableArtistName
                    key={idx}
                    name={bandName}
                    className="b-artist-card__member-chip"
                  />
                )
              ))}
              {memberOf.length > 5 && (
                <span className="b-artist-card__member-more">+{memberOf.length - 5}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
